"use client";

import {
  EFFECTS_DETECTION_SEGMENT_DURATION_S,
  MODEL_EFFECTS_CHUNK_DURATION_S,
} from "@/constants/audioQuality";
import { postAudioQualityCheck } from "@/data/audioQuality/actions/postAudioQualityCheck";
import { AudioQualityCheck } from "@/types/defs/audioQuality/audioQualityCheck";
import { audioBufferToWav } from "@/utils/audio/audioBufferToWav";
import { decodeAudioFile } from "@/utils/audio/decodeAudioFile";
import { extractAudioSegment } from "@/utils/audio/extractAudioSegment";
import { buildFormDataForAudioQualityCheck } from "@/utils/audio/quality/buildFormDataForAudioQualityCheck";
import { computeAudioChunkRanges } from "@/utils/audio/quality/computeAudioChunkRanges";
import { extractHighestEnergySegment } from "@/utils/audio/quality/extractHighestEnergySegment";

// Effects-only quality check for model training audio. Files can be up to 30 min and there is no artist/pitch, so we split the audio into 3-minute chunks and send the highest-energy 2s of every chunk in a single request (one `audio_for_effects_detection` part per chunk); the backend aggregates them into a single instrument/reverb result.
export const checkAudioQualityForModelCreation = async ({
  audioFile,
  signal,
}: {
  audioFile: File;
  signal: AbortSignal | null;
}): Promise<AudioQualityCheck> => {
  const audioBuffer = await decodeAudioFile(audioFile);

  const chunkRanges = computeAudioChunkRanges({
    totalDurationSeconds: audioBuffer.duration,
    chunkDurationSeconds: MODEL_EFFECTS_CHUNK_DURATION_S,
  });

  // Chunk extraction + WAV encoding go through the Web Audio API (an AudioContext per call), so we prepare the segments sequentially to stay under the browser's concurrent-AudioContext limit.
  const audioForEffectsDetection: Blob[] = [];
  for (const { startTimeSeconds, endTimeSeconds } of chunkRanges) {
    // Bail out between chunks when the input was cleared/swapped. AbortError so the caller's isAbortError swallows it exactly like a cancelled upload or quality-check request.
    if (signal !== null && signal.aborted) {
      throw new DOMException("Audio quality check aborted", "AbortError");
    }

    const chunkBuffer = await extractAudioSegment({
      audioBuffer,
      startTimeSeconds,
      endTimeSeconds,
    });

    const effectsDetectionSegment = await extractHighestEnergySegment({
      audioBuffer: chunkBuffer,
      duration: EFFECTS_DETECTION_SEGMENT_DURATION_S,
    });

    audioForEffectsDetection.push(
      await audioBufferToWav({
        audioBuffer: effectsDetectionSegment.audioBuffer,
        targetFormat: null,
      })
    );
  }

  const formData = buildFormDataForAudioQualityCheck({
    audioForPitchAnalysis: null,
    audioForEffectsDetection,
    sweetSpot: null,
  });

  const audioQualityResult = await postAudioQualityCheck(formData, signal);

  return {
    ...audioQualityResult,
    // One clip per chunk was sent, so there is no single in-track segment to record for analytics the way the conversion check does.
    effectsSegment: null,
    pitchSegment: null,
  };
};
